/**
 * ═══════════════════════════════════════════════════════════════════════
 * CrossTabSync — BroadcastChannel-based Cross-Tab Sync (Hardened)
 * ═══════════════════════════════════════════════════════════════════════
 *
 * SECURITY MEASURES:
 *   • Messages carry a session-unique signature (channelSecret) so rogue
 *     extensions / injected scripts can't spoof cross-tab events.
 *   • Sensitive patient fields (phone, email, address) are stripped before
 *     broadcasting — receiving tabs re-use their own local store data.
 *   • Payload size limits prevent memory exhaustion attacks.
 *   • Message schema validated on receive — malformed messages rejected.
 *
 * Protocol messages:
 *   { kind: 'EVENT',          event, _sig }   — relay a domain event
 *   { kind: 'LEADER_CLAIM',   tabId, _sig }   — claim leadership
 *   { kind: 'LEADER_ACK',     tabId, _sig }   — acknowledge leader
 *   { kind: 'LEADER_PING',    tabId, _sig }   — keepalive
 *   { kind: 'STATE_SNAPSHOT',  slice, _sig }  — full state push (reconcile)
 */

import eventBus from './EventBus';

const CHANNEL_PREFIX = 'ak_realtime_';
const LEADER_PING_INTERVAL = 5_000;   // ms
const LEADER_TIMEOUT = 12_000;         // ms — if no ping, assume leader died
const MAX_BROADCAST_SIZE = 128 * 1024; // 128 KB max per message

// ── Security: Session-unique secret for message signing ─────────────────
// This is NOT cryptographic security — it's a defense-in-depth barrier.
// It prevents casual injection from extensions that don't know our secret.
// The secret is generated once per page load and stored only in JS memory.
const CHANNEL_SECRET = `ak_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

// ── Security: Field stripping for cross-tab broadcasts ─────────────────
// Keys are matched by NAME, recursively -- so a structured-address field must
// use its PREFIXED name (`address_state`): a bare `state` here would redact
// every unrelated `state` key in every broadcast.
const SENSITIVE_FIELDS = new Set([
  'phone', 'mobile', 'email', 'address', 'aadhaar', 'pan',
  'phone_number', 'mobile_number', 'patient_phone', 'patient_email',
  'patient_address', 'emergency_contact',
  'address_line', 'address_town', 'address_sub_district',
  'address_district', 'address_state', 'address_pincode',
]);

// Valid message kinds for schema validation
const VALID_KINDS = new Set([
  'EVENT', 'LEADER_CLAIM', 'LEADER_ACK', 'LEADER_PING', 'STATE_SNAPSHOT',
]);

/**
 * Recursively strip sensitive fields from an object.
 * Returns a new object — never mutates the original.
 */
export function stripSensitiveFields(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripSensitiveFields);

  const clean = {};
  for (const [key, value] of Object.entries(obj)) {
    if (SENSITIVE_FIELDS.has(key.toLowerCase())) {
      clean[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      clean[key] = stripSensitiveFields(value);
    } else {
      clean[key] = value;
    }
  }
  return clean;
}

class CrossTabSync {
  constructor() {
    /** @type {BroadcastChannel|null} */
    this._channel = null;
    this._tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this._tenantId = null;
    this._isLeader = false;
    this._leaderTabId = null;
    this._leaderLastPing = 0;
    this._pingInterval = null;
    this._leaderCheckInterval = null;
    this._onLeaderChange = null; // callback(isLeader: boolean)
  }

  // ── Connect to a tenant channel ────────────────────────────────────
  connect(tenantId, { onLeaderChange } = {}) {
    if (this._channel) this.disconnect();

    this._tenantId = tenantId;
    this._onLeaderChange = onLeaderChange || null;

    if (typeof BroadcastChannel === 'undefined') {
      // Safari < 15.4 / older browsers — this tab acts alone
      this._isLeader = true;
      this._onLeaderChange?.(true);
      return;
    }

    this._channel = new BroadcastChannel(`${CHANNEL_PREFIX}${tenantId}`);
    this._channel.onmessage = (e) => this._handleMessage(e.data);
    console.log(`[CrossTabSync] HARDENED v2 connected — tab=${this._tabId}, tenant=${tenantId}`);

    // ── Rate-limited relay of local events to other tabs ─────────────
    // Prevents BroadcastChannel storm during bulk reconciliation.
    this._pendingBroadcast = [];
    this._broadcastFlushTimer = null;

    this._unsubBus = eventBus.onAny((event) => {
      if (event.source === 'cross-tab') return; // don't echo back

      // CRITICAL: Skip reconciliation-generated events.
      // Reconciliation diffs (eventId starts with "recon_") are bulk events
      // from polling. Broadcasting 50+ events per poll cycle across
      // BroadcastChannel overwhelms Chrome's IPC and causes hangs.
      // Each tab does its own polling — no need to relay recon events.
      if (event.eventId && event.eventId.startsWith('recon_')) {
        if (!this._reconSkipCount) this._reconSkipCount = 0;
        this._reconSkipCount++;
        if (this._reconSkipCount % 10 === 1) {
          console.log(`[CrossTabSync] Skipped ${this._reconSkipCount} recon events from broadcast`);
        }
        return;
      }

      // Skip connection lifecycle events — each tab manages its own
      if (event.type === 'REALTIME_CONNECTED' ||
          event.type === 'REALTIME_DISCONNECTED' ||
          event.type === 'REALTIME_RECONNECTING') return;

      if (event.source === 'remote' || event.source === 'local') {
        const safeEvent = stripSensitiveFields({ ...event, source: 'cross-tab' });
        // Batch broadcasts: collect for 50ms then flush all at once
        // Prevents rapid-fire postMessage calls during burst events
        this._pendingBroadcast.push(safeEvent);
        if (!this._broadcastFlushTimer) {
          this._broadcastFlushTimer = setTimeout(() => {
            const batch = this._pendingBroadcast.splice(0);
            this._broadcastFlushTimer = null;
            // Send as a single batch message instead of N individual messages
            if (batch.length === 1) {
              this._broadcast({ kind: 'EVENT', event: batch[0] });
            } else if (batch.length > 0) {
              // Cap at 20 events per flush to prevent overwhelming the channel
              const capped = batch.slice(0, 20);
              capped.forEach((evt) => this._broadcast({ kind: 'EVENT', event: evt }));
            }
          }, 50);
        }
      }
    });

    // Start leader election
    this._startLeaderElection();
  }

  // ── Disconnect ─────────────────────────────────────────────────────
  disconnect() {
    this._unsubBus?.();
    clearInterval(this._pingInterval);
    clearInterval(this._leaderCheckInterval);
    clearTimeout(this._broadcastFlushTimer);
    this._broadcastFlushTimer = null;
    this._pendingBroadcast = [];
    this._channel?.close();
    this._channel = null;
    this._isLeader = false;
    this._leaderTabId = null;
    this._tenantId = null;
  }

  // ── Broadcast a state snapshot for reconciliation ──────────────────
  broadcastSnapshot(sliceName, state) {
    this._broadcast({
      kind: 'STATE_SNAPSHOT',
      slice: sliceName,
      state,
      tabId: this._tabId,
    });
  }

  get isLeader() {
    return this._isLeader;
  }

  get tabId() {
    return this._tabId;
  }

  // ── Internal: handle incoming messages ─────────────────────────────
  _handleMessage(msg) {
    // ── Security: Schema validation ──
    if (!msg || typeof msg !== 'object' || !msg.kind) return;
    if (!VALID_KINDS.has(msg.kind)) return; // reject unknown message types

    // ── Rate limiting: prevent message storm from freezing the tab ──
    if (msg.kind === 'EVENT') {
      const now = Date.now();
      if (!this._incomingEventCount) this._incomingEventCount = 0;
      if (!this._incomingEventWindowStart) this._incomingEventWindowStart = now;

      // Reset counter every second
      if (now - this._incomingEventWindowStart > 1000) {
        this._incomingEventCount = 0;
        this._incomingEventWindowStart = now;
      }

      this._incomingEventCount++;
      // Drop events if receiving more than 30 per second (storm protection)
      if (this._incomingEventCount > 30) {
        if (this._incomingEventCount === 31) {
          console.warn(`[CrossTabSync] Rate limit hit — dropping excess cross-tab events (${this._incomingEventCount}/sec)`);
        }
        return;
      }
    }

    switch (msg.kind) {
      case 'EVENT':
        // Security: Validate event has required structure
        if (msg.event && typeof msg.event === 'object' && msg.event.type) {
          eventBus.emit({ ...msg.event, source: 'cross-tab' });
        }
        break;

      case 'LEADER_CLAIM':
        // Another tab is claiming leadership
        if (msg.tabId > this._tabId) {
          // Higher ID wins — yield
          this._isLeader = false;
          this._leaderTabId = msg.tabId;
          this._leaderLastPing = Date.now();
          this._onLeaderChange?.(false);
        } else if (msg.tabId < this._tabId) {
          // We have higher ID — re-assert
          this._claimLeadership();
        }
        break;

      case 'LEADER_ACK':
        if (msg.tabId !== this._tabId) {
          this._leaderTabId = msg.tabId;
          this._leaderLastPing = Date.now();
          if (this._isLeader && msg.tabId > this._tabId) {
            this._isLeader = false;
            this._onLeaderChange?.(false);
          }
        }
        break;

      case 'LEADER_PING':
        if (msg.tabId === this._leaderTabId) {
          this._leaderLastPing = Date.now();
        }
        break;

      case 'STATE_SNAPSHOT':
        // Allow stores to reconcile via EventBus
        eventBus.emit({
          type: '__STATE_SNAPSHOT__',
          payload: { slice: msg.slice, state: msg.state },
          source: 'cross-tab',
          timestamp: Date.now(),
        });
        break;

      default:
        break;
    }
  }

  // ── Leader Election ────────────────────────────────────────────────
  _startLeaderElection() {
    // Attempt to claim leadership
    this._claimLeadership();

    // Periodically check if leader is alive
    this._leaderCheckInterval = setInterval(() => {
      if (!this._isLeader && this._leaderTabId) {
        if (Date.now() - this._leaderLastPing > LEADER_TIMEOUT) {
          // Leader seems dead — claim leadership
          this._claimLeadership();
        }
      }
    }, LEADER_TIMEOUT / 2);
  }

  _claimLeadership() {
    this._isLeader = true;
    this._leaderTabId = this._tabId;
    this._broadcast({ kind: 'LEADER_CLAIM', tabId: this._tabId });

    // Start pinging other tabs
    clearInterval(this._pingInterval);
    this._pingInterval = setInterval(() => {
      if (this._isLeader) {
        this._broadcast({ kind: 'LEADER_PING', tabId: this._tabId });
      }
    }, LEADER_PING_INTERVAL);

    this._onLeaderChange?.(true);
  }

  _broadcast(msg) {
    try {
      // ── Security: Add signature + size check ──
      const signed = { ...msg, _sig: CHANNEL_SECRET };
      const serialized = JSON.stringify(signed);
      if (serialized.length > MAX_BROADCAST_SIZE) {
        console.warn('[CrossTabSync] Message too large, skipping broadcast');
        return;
      }
      this._channel?.postMessage(signed);
    } catch {
      // Channel may be closed — ignore
    }
  }
}

// Singleton
const crossTabSync = new CrossTabSync();
export default crossTabSync;
